/**
 * Extract a user-facing message from an axios response (or error.response).
 */
export function getApiErrorMessage(response, fallback = "Something went wrong. Please try again.") {
  return extractApiMessage(response, fallback);
}

/** Same shape as error responses; used for 200/201 success bodies. */
export function getApiSuccessMessage(response, fallback = "Success!") {
  return extractApiMessage(response, fallback);
}

function extractApiMessage(response, fallback) {
  if (!response) return fallback;
  const data = response.data;
  if (!data) return fallback;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (typeof data.message === "string" && data.message.trim()) return data.message.trim();
  if (typeof data.error === "string" && data.error.trim()) return data.error.trim();
  if (Array.isArray(data.errors) && data.errors[0]?.message) {
    return String(data.errors[0].message);
  }
  return fallback;
}

export function getApiErrorTitle(status, message = "") {
  if (status === 409) return "Email already registered";
  if (status === 403) return "Registration unavailable";
  if (status === 400) return "Invalid details";
  if (status >= 500) return "Server error";
  if (/email already/i.test(message)) return "Email already registered";
  return "Registration failed";
}

export function getSignupSuccessCopy({ response, user, email }) {
  const apiMessage = getApiSuccessMessage(response, "");
  const userEmail = email || user?.email || "your email";
  const needsVerify = user?.is_verified === false;

  if (apiMessage) {
    return {
      title: "Account created!",
      text: apiMessage,
    };
  }

  if (needsVerify) {
    return {
      title: "Account created!",
      text: `A verification email has been sent to ${userEmail}. Please check your inbox and verify your account before logging in.`,
    };
  }

  return {
    title: "Account created!",
    text: "Your account is ready. You can log in now.",
  };
}

export function getForgotPasswordSuccessCopy(response) {
  return {
    title: "Check your email",
    text: getApiSuccessMessage(
      response,
      "If this email exists in our system, we've sent you a password reset link.",
    ),
  };
}

export function getForgotPasswordErrorCopy(response) {
  const status = response?.status;
  const message = getApiErrorMessage(
    response,
    "Could not send the reset link. Please try again.",
  );
  let title = "Could not send reset link";
  if (status === 400) title = "Invalid email";
  if (status >= 500) title = "Server error";
  return { title, text: message };
}

export function getResetPasswordSuccessCopy(response) {
  return {
    title: "Password reset successful",
    text: getApiSuccessMessage(
      response,
      "Your password has been reset successfully. You can now log in with your new password.",
    ),
  };
}

export function getResetPasswordErrorCopy(response) {
  const status = response?.status;
  const message = getApiErrorMessage(
    response,
    "Failed to reset password. Please try again.",
  );
  return {
    title: status === 400 ? "Invalid or expired link" : "Reset failed",
    text: message,
  };
}
